import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { FormGroup, Button, RotateLoader, Switch, ButtonGroup } from '@lib/components';
import cloneDeep from 'lodash/cloneDeep';
import { MobxComponent } from '../../../../../../../mobx/components/index';
import { UI } from '../../../../../../../core/ui';

type StoredReceiptMode = NonNullable<T.Schema.Restaurant.RestaurantSettings['receipt_mode']>;
/** Form state is always fully populated by `normalize`, so `access` is required here. */
type ReceiptMode = Omit<StoredReceiptMode, 'access'> & {
  access: NonNullable<StoredReceiptMode['access']>;
};

interface Props {}
interface State {
  values: ReceiptMode;
  saved: ReceiptMode;
  confirmOpen: boolean;
  saving: boolean;
}

const DEFAULT_MODE: ReceiptMode = {
  mode: 'legacy',
  access: { qr_in_email: false, qr_on_docket: false, download_pdf_in_email: true },
  template: 'detailed',
  rollout_percent: 100,
};

function normalize(mode?: StoredReceiptMode | null): ReceiptMode {
  return {
    mode: mode?.mode === 'modern' ? 'modern' : 'legacy',
    access: {
      qr_in_email: false,
      qr_on_docket: !!mode?.access?.qr_on_docket,
      download_pdf_in_email: mode?.access?.download_pdf_in_email !== false,
    },
    template: mode?.template === 'compact' ? 'compact' : 'detailed',
    rollout_percent: 100,
  };
}

@inject('store')
@observer
export class SettingsFormReceipt extends MobxComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    const current = normalize(this.injected.store.restaurant!.settings.receipt_mode);
    this.state = {
      values: current,
      saved: current,
      confirmOpen: false,
      saving: false,
    };
  }

  componentDidMount() {
    this.loadSaved();
  }

  loadSaved = async () => {
    const restaurant_id = this.injected.store.restaurant!._id;
    try {
      const res = await this.injected.store.api.receipt_settings_find({ restaurant_id });
      if (!res.outcome && res.receipt_mode) {
        const values = normalize(res.receipt_mode);
        this.setState({ values, saved: values });
        return;
      }
    } catch {
      // fall through to restaurant copy
    }
  };

  update = (patch: Partial<ReceiptMode>) => {
    this.setState(state => ({
      values: normalize({ ...state.values, ...patch, access: { ...state.values.access, ...patch.access } }),
    }));
  };

  requestSave = () => {
    if (this.state.saved.mode === 'legacy' && this.state.values.mode === 'modern') {
      this.setState({ confirmOpen: true });
      return;
    }
    this.save();
  };

  save = async () => {
    const restaurant_id = this.injected.store.restaurant!._id;
    this.setState({ saving: true, confirmOpen: false });
    try {
      const res = await this.injected.store.api.receipt_settings_update({
        restaurant_id,
        receipt_mode: this.state.values,
      });
      if (res.outcome || !res.receipt_mode) {
        UI.notification.error(res.message || 'An error occurred');
        this.setState({ saving: false });
        return;
      }
      const values = normalize(res.receipt_mode);
      const r = cloneDeep(this.injected.store.restaurant!);
      r.settings.receipt_mode = values;
      this.injected.store.setRestaurant(r);
      this.setState({ values, saved: values, saving: false });
      UI.notification.success('Settings Updated');
    } catch {
      UI.notification.error('An error occurred');
      this.setState({ saving: false });
    }
  };

  render() {
    const { values, confirmOpen, saving } = this.state;
    return (
      <div className="p-4">
        <FormGroup
          title="Customer order email"
          help="Choose how customers receive their order confirmation email."
        >
          <ButtonGroup
            size="xs"
            width={90}
            buttonWidth={false}
            selected={values.mode}
            options={[
              { value: 'legacy', name: 'Standard' },
              { value: 'modern', name: 'Enhanced' },
            ]}
            onSelect={option => this.update({ mode: option.value as ReceiptMode['mode'] })}
          />
          <p className="small m-t-2">
            {values.mode === 'legacy'
              ? 'Your current order confirmation email format.'
              : 'Receipt shown in the email body, with a link to view online and optionally download a PDF.'}
          </p>
        </FormGroup>

        {values.mode === 'modern' && (
          <FormGroup
            title="Download PDF button in email"
            help="Shows a Download PDF button in the email. The PDF is created when the customer clicks the button — it is not attached to the email."
          >
            <Switch
              id="receipt-download-pdf-in-email"
              checked={values.access.download_pdf_in_email !== false}
              onChange={e =>
                this.update({ access: { ...values.access, download_pdf_in_email: e.target.checked } })
              }
            />
          </FormGroup>
        )}

        <FormGroup
          title="QR code on printed dockets"
          help="Prints a scannable QR code on dockets for customers to open their receipt online. You can turn this off per printer (e.g. kitchen prep tickets). No QR is added to emails."
        >
          <Switch
            id="receipt-qr-on-docket"
            checked={!!values.access.qr_on_docket}
            onChange={e => this.update({ access: { ...values.access, qr_on_docket: e.target.checked } })}
          />
        </FormGroup>

        <FormGroup
          title="Email template style"
          help="Compact omits line items. Both styles use your store branding colours."
        >
          <ButtonGroup
            size="xs"
            width={90}
            buttonWidth={false}
            selected={values.template}
            options={[
              { value: 'compact', name: 'Compact' },
              { value: 'detailed', name: 'Detailed' },
            ]}
            onSelect={option => this.update({ template: option.value as ReceiptMode['template'] })}
          />
        </FormGroup>

        {confirmOpen && (
          <FormGroup
            title="Switch to enhanced receipts?"
            help="Customer emails will use the enhanced layout. PDFs are downloaded on demand, not attached."
          >
            <div className="flex-line">
              <Button color="primary" size="xs" onClick={this.save} className="m-r-2">
                Confirm
              </Button>
              <Button size="xs" onClick={() => this.setState({ confirmOpen: false })}>
                Cancel
              </Button>
            </div>
          </FormGroup>
        )}

        <Button full={true} color="primary" onClick={this.requestSave} disabled={saving}>
          {saving ? <RotateLoader size={2} color="white" /> : 'Save'}
        </Button>
      </div>
    );
  }
}
